import { Form, Formik } from "formik";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import * as Yup from "yup";
import CalculateForKnown from "./calculate-for-known";
import CalculateForUnknown from "./calculate-for-unknown";
import { OptionCard } from "../ui/option-card";
import { useCreateShipmentCalculation } from "@/hooks/useCreateShipmentCalculation";
import { useState, type Dispatch, type SetStateAction } from "react";
import type {
  CalculatorSectionType,
  ShipmentBasicInfo,
  ShipmentCalculationResult,
  FullFormValues,
} from "../types/shipment-calculator.type";
import { getFreightConfigs } from "../config/freightConfigs";
import { useChannelProcessingFeesSorted } from "@/hooks/useChannelProcessingFeesSorted";
import { getEstimatedWeightRange } from "../helpers/product-weight-estimates";
import { toast } from "sonner";

type CalculateShipmentCostProps = {
  shipmentBasicInfo: ShipmentBasicInfo;
  setResults: Dispatch<SetStateAction<ShipmentCalculationResult | null>>;
  setStep: Dispatch<SetStateAction<number>>;
  data: CalculatorSectionType;
};

function CalculateShipmentCost({
  shipmentBasicInfo,
  setResults,
  setStep,
  data,
}: CalculateShipmentCostProps) {
  const [calculationData, setCalculationData] = useState<FullFormValues | null>(null);
  const freightConfigs = getFreightConfigs(data);
  const freightMethod = shipmentBasicInfo.method === "Sea" ? "Sea" : "Air";
  const config = freightConfigs[freightMethod];
  const {
    data: { airFees, seaFees } = { airFees: [], seaFees: [] },
    isLoading,
    isError: isChannelProcessingFeesError,
  } = useChannelProcessingFeesSorted();
  const channnelProcessingFees = freightMethod === "Air" ? airFees : seaFees;
  const validationSchema = Yup.object({
    mode: Yup.string().required(),
    [config.fieldName]: Yup.number().when("mode", {
      is: config.primaryOption.value,
      then: (schema) =>
        schema
          .typeError("Enter a valid number")
          .positive("Enter a value greater than zero")
          .required("This field is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const {
    mutate: createShipmentCalculation,
    isError,
    isPending,
  } = useCreateShipmentCalculation({
    onSuccess: ({ data }) => {
      if ("int_shipping_fee_2" in data) {
        setResults({
          method: freightMethod,
          kind: shipmentBasicInfo.kind,
          country: shipmentBasicInfo.country,
          currency: "USD",
          processing_fee_1: data.processing_fee_1,
          processing_fee_2: data.processing_fee_2,
          vat_1: data.vat_1,
          vat_2: data.vat_2,
          int_shipping_fee_1: data.int_shipping_fee_1,
          int_shipping_fee_2: data.int_shipping_fee_2,
          clearance_cost_1: data.clearance_cost_1,
          clearance_cost_2: data.clearance_cost_2,
          estimated_weight_range: {
            min: calculationData?.estimatedMinWeight
              ? +Number(calculationData.estimatedMinWeight).toFixed(2)
              : undefined,
            max: calculationData?.estimatedMaxWeight
              ? +Number(calculationData.estimatedMaxWeight).toFixed(2)
              : undefined,
          },
        });
      } else {
        setResults({
          method: freightMethod,
          kind: shipmentBasicInfo.kind,
          country: shipmentBasicInfo.country,
          currency: "USD",
          processing_fee_1: data.processing_fee_1,
          vat_1: data.vat_1,
          int_shipping_fee_1: data.int_shipping_fee_1,
          clearance_cost_1: data.clearance_cost_1,
          weight: calculationData?.weight ? Number(calculationData.weight) : undefined,
          cbm: calculationData?.cbm ? Number(calculationData.cbm) : undefined,
          estimated_weight: calculationData?.estimated_weight
            ? +Number(calculationData.estimated_weight).toFixed(2)
            : undefined,
        });
      }
      setStep(3);
    },
  });

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[506px] animate-pulse">
        <Skeleton className="mb-8 h-[120px] w-full" />
        <Skeleton className="mb-4 h-[60px] w-full" />
      </div>
    );
  }

  if (isChannelProcessingFeesError) {
    return (
      <div className="mx-auto w-full max-w-[506px]">
        <p className="text-center text-red-500">
          Failed to load processing fees. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <Formik
      initialValues={{
        mode: config.primaryOption.value,
        weight: "",
        cbm: "",
        productType: "",
        quantity: "",
        estimated_weight: "",
        estimatedMinWeight: "",
        estimatedMaxWeight: "",
        measurementUnit: "",
        length: "",
        width: "",
        height: "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        setCalculationData(values);
        const showPrimaryForm = values.mode === config.primaryOption.value;
        if (showPrimaryForm) {
          if (freightMethod === "Air") {
            createShipmentCalculation({
              origin_country: "CN",
              destination_country: shipmentBasicInfo.country,
              channel: "Air",
              model: shipmentBasicInfo.kind === "Normal" ? "Normal" : "Special",
              weight_1: values.weight ? Number(values.weight) : 0,
            });
          } else {
            createShipmentCalculation({
              origin_country: "CN",
              destination_country: shipmentBasicInfo.country,
              channel: "Sea",
              model: "Sea",
              cbm: values.cbm ? Number(values.cbm) : 0,
            });
          }
        } else {
          if (freightMethod === "Air") {
            const estimatedWeightRange = getEstimatedWeightRange(
              values.productType,
              values.quantity,
            );
            if (!estimatedWeightRange) {
              toast.error(
                "Unable to estimate weight. Please check your product type and quantity.",
              );
              return;
            }

            createShipmentCalculation({
              origin_country: "CN",
              destination_country: shipmentBasicInfo.country,
              channel: "Air",
              model: shipmentBasicInfo.kind === "Normal" ? "Normal" : "Special",
              weight_1: estimatedWeightRange.max,
              weight_2: estimatedWeightRange.min,
            });
          } else {
            let length = values.length ? Number(values.length) : 0;
            let width = values.width ? Number(values.width) : 0;
            let height = values.height ? Number(values.height) : 0;
            if (values.measurementUnit === "cm") {
              length = length / 100;
              width = width / 100;
              height = height / 100;
            }
            createShipmentCalculation({
              origin_country: "CN",
              destination_country: shipmentBasicInfo.country,
              channel: "Sea",
              model: "Sea",
              length: length,
              width: width,
              height: height,
              quantity: values.quantity ? Number(values.quantity) : 1,
            });
          }
        }

        if (isError) {
          toast.error("Failed to calculate shipment cost. Please try again later.");
          return;
        }
      }}
    >
      {({ errors, setFieldValue, touched, values }) => {
        const showPrimaryForm = values.mode === config.primaryOption.value;

        return (
          <Form className="mx-auto flex w-full max-w-[506px] flex-col px-3 sm:px-0">
            <div className="mb-8 text-center sm:mb-9">
              <h2 className="text-[24px] font-medium leading-tight text-black sm:text-[28px]">
                {config.title}
              </h2>
              <p className="mt-2 text-base leading-snug text-[#323232] sm:text-xl">
                {config.subtitle}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {[config.primaryOption, config.secondaryOption].map((option) => (
                <OptionCard
                  key={option.value}
                  name="mode"
                  value={option.value}
                  title={option.title}
                  description={option.description}
                  checked={values.mode === option.value}
                  onChange={(value) => setFieldValue("mode", value)}
                />
              ))}
            </div>

            <div className="mt-9">
              {showPrimaryForm ? (
                <CalculateForKnown
                  config={config}
                  touched={touched}
                  errors={errors}
                  values={values}
                  setFieldValue={setFieldValue}
                  processingFees={channnelProcessingFees}
                />
              ) : (
                <CalculateForUnknown
                  freightMethod={freightMethod}
                  processingFees={channnelProcessingFees}
                />
              )}
            </div>

            <Button
              type="submit"
              radius="full"
              className="mt-[56px] h-[70px] w-full bg-[#0f4694] text-[26px] font-normal hover:bg-[#0d3d82]"
            >
              {isPending ? "Calculating..." : "Calculate Cost"}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
}

export default CalculateShipmentCost;
